// Reporting period bounds (per data model) and the per-metric reporting period override.
//
// This MIRRORS ReportSqlBuilder.PeriodBounds.cs on the backend. The backend builds the SQL
// that runs; this builds the SQL shown in the preview tab - keep the two identical.
//
//   Run date   The SQL Server clock (GETDATE()) - the query runs on the server.
//   Bounds     report.modelPeriodBounds[modelKey]; absent keeps the legacy clause.
//                Written with the operator picked, by day: CAST(column AS date) >= | > start
//                and CAST(column AS date) <= | < end
//                      "none" -> no upper bound; absent -> the period's legacy default
//   Columns    startColumn / endColumn: the date each bound compares (absent = Based On column)
//   If blank   per bound (startIfBlank / endIfBlank): COALESCE(column, other column | today | fixed date)
//   Metric     section.periodFilter.mode "custom" drops the report period for that metric
//              and applies its own condition tree instead (empty tree = no date filter).
import type { ReportLogicContext } from "./context";
import type {
    DataColumn,
    PeriodBoundSide,
    PeriodIfBlank,
    MetricPeriodFilter,
    MetricSection,
    ModelKey,
    PeriodBounds,
    PeriodEndOperator,
    PeriodIfBlankKind,
    PeriodStartOperator,
    ReportConfig,
} from "../types";
import { columnMatchesKey, findTimestampColumn, timestampColumnsForModel } from "./catalogResolution";
import { TODAY_SQL, dateSqlForColumn, dateSqlLiteral, periodSqlClause } from "./sqlExpressions";
import { normalizeReportingPeriodType, validLastN } from "./reportingPeriods";
import { collectRuleNodeIssues, metricFilterScope, ruleNodesExpression, ruleNodesOutline } from "./ruleInterpretation";
import type { RuleNodeIssue } from "./ruleInterpretation";

const PERIOD_START_OPERATOR_OPTIONS: { value: PeriodStartOperator; name: string }[] = [
    { value: "gte", name: "On or after (>=)" },
    { value: "gt", name: "After (>)" },
];

const PERIOD_END_OPERATOR_OPTIONS: { value: PeriodEndOperator; name: string }[] = [
    { value: "lte", name: "On or before (<=)" },
    { value: "lt", name: "Before (<)" },
    { value: "none", name: "No end date" },
];

const PERIOD_IF_BLANK_OPTIONS: { value: PeriodIfBlankKind; name: string }[] = [
    { value: "none", name: "Leave it out (no fallback)" },
    { value: "column", name: "Use another date column" },
    { value: "today", name: "Use today's date" },
    { value: "date", name: "Use a fixed date" },
];

/** End operator when none is chosen: periods that never had an upper bound keep none. */
function defaultEndOperator(type: string): PeriodEndOperator {
    const normalized = normalizeReportingPeriodType(type);
    return normalized === "Last7Days" || normalized === "Last30Days" || normalized === "LastNDays" || normalized === "CurrentQuarter" ? "none" : "lte";
}

function makePeriodBounds(report: ReportConfig): PeriodBounds {
    return { startOperator: "gte", startIfBlank: { kind: "none" }, endIfBlank: { kind: "none" } };
}

function periodBoundsFor(report: ReportConfig, modelKey: ModelKey | ""): PeriodBounds | null {
    return (modelKey && report.modelPeriodBounds?.[modelKey]) || null;
}

/** N as shown in the preview SQL ("N" until a valid number is entered - validation blocks the save). */
function lastNText(type: string, lastN?: number) {
    const n = validLastN(type, lastN);
    return n !== null ? String(n) : "N";
}

/** Inclusive first and last DAY of the period, as SQL date expressions (server date). */
function periodBoundDates(type: string, customStart: string, customEnd: string, lastN?: number) {
    const t = TODAY_SQL;
    const monthStart = `DATEFROMPARTS(YEAR(${t}), MONTH(${t}), 1)`;
    const quarterStart = `DATEFROMPARTS(YEAR(${t}), (DATEPART(QUARTER, ${t}) - 1) * 3 + 1, 1)`;
    switch (normalizeReportingPeriodType(type)) {
        case "PreviousMonth": return { start: `DATEADD(MONTH, -1, ${monthStart})`, end: `EOMONTH(${t}, -1)` };
        case "CurrentMonthToDate": return { start: monthStart, end: t };
        case "Last7Days": return { start: `DATEADD(DAY, -7, ${t})`, end: t };
        case "CurrentQuarter": return { start: quarterStart, end: `EOMONTH(${quarterStart}, 2)` };
        case "PreviousQuarter": return { start: `DATEADD(QUARTER, -1, ${quarterStart})`, end: `DATEADD(DAY, -1, ${quarterStart})` };
        case "YearToDate": return { start: `DATEFROMPARTS(YEAR(${t}), 1, 1)`, end: t };
        case "LastNDays": return { start: `DATEADD(DAY, -${lastNText(type, lastN)}, ${t})`, end: t };
        // N complete calendar months before the current one; N = 0: the current month to date.
        case "LastNMonths": return lastNText(type, lastN) === "0"
            ? { start: monthStart, end: t }
            : { start: `DATEADD(MONTH, -${lastNText(type, lastN)}, ${monthStart})`, end: `EOMONTH(${t}, -1)` };
        case "CustomRange": return {
            start: customStart ? dateSqlLiteral(customStart) : "@StartDate",
            end: customEnd ? dateSqlLiteral(customEnd) : "@EndDate",
        };
        default: return { start: `DATEADD(DAY, -30, ${t})`, end: t };
    }
}

/** True when the end bound is applied (an end operator other than "no end date"). */
function hasEndBound(report: ReportConfig, bounds: PeriodBounds) {
    return (bounds.endOperator || defaultEndOperator(report.reportingPeriod.type)) !== "none";
}

/** The expression compared by day; already-cast expressions are left as they are. */
function asDaySql(sql: string) {
    return sql.startsWith("CAST(") && sql.endsWith(" AS date)") && !sql.includes("\n") ? sql : `CAST(${sql} AS date)`;
}

function periodBoundsClause(report: ReportConfig, bounds: PeriodBounds, startSql: string, endSql: string) {
    const { type, customStart, customEnd, lastN } = report.reportingPeriod;
    const { start, end } = periodBoundDates(type, customStart, customEnd, lastN);
    // Each bound uses the operator picked, compared by day (mirrors ReportSqlBuilder.PeriodBoundsClause).
    const clauses: string[] = [];
    clauses.push(`${asDaySql(startSql)} ${bounds.startOperator === "gt" ? ">" : ">="} ${start}`);
    const endOperator = bounds.endOperator || defaultEndOperator(type);
    if (endOperator === "lt" || endOperator === "lte") clauses.push(`${asDaySql(endSql)} ${endOperator === "lt" ? "<" : "<="} ${end}`);
    return clauses.length > 1 ? `(${clauses.join("\n  AND ")})` : clauses[0];
}

/** Period clause for one date expression on this model (Period Date): configured operators when present, else legacy. */
function periodClauseFor(context: ReportLogicContext, report: ReportConfig, modelKey: ModelKey | "", columnSql: string) {
    const bounds = periodBoundsFor(report, modelKey);
    const { type, customStart, customEnd, lastN } = report.reportingPeriod;
    return bounds ? periodBoundsClause(report, bounds, columnSql, columnSql) : periodSqlClause(type, columnSql, customStart, customEnd, lastN);
}

function timestampColumn(context: ReportLogicContext, modelKey: ModelKey | "", key?: string): DataColumn | null {
    if (!key) return null;
    return timestampColumnsForModel(context, modelKey).find((column) => columnMatchesKey(column, key)) || null;
}

/** The model's Based On column (the default for both bounds). */
function basedOnColumnFor(context: ReportLogicContext, report: ReportConfig, modelKey: ModelKey | ""): DataColumn | null {
    const model = modelKey ? context.dataModels[modelKey] : undefined;
    if (!model?.timestampColumns.length) return null;
    return findTimestampColumn(model, report.modelBasedOn[modelKey as ModelKey]) || model.timestampColumns[0];
}

/** The date column a bound compares: the chosen one, or the Based On column when none is chosen. */
function boundColumn(context: ReportLogicContext, report: ReportConfig, modelKey: ModelKey | "", side: PeriodBoundSide): DataColumn | null {
    const bounds = periodBoundsFor(report, modelKey);
    const key = side === "start" ? bounds?.startColumn : bounds?.endColumn;
    return key ? timestampColumn(context, modelKey, key) : basedOnColumnFor(context, report, modelKey);
}

/** A bound's "if blank" fallback: its own, else the legacy shared one. */
function boundIfBlank(bounds: PeriodBounds | null | undefined, side: PeriodBoundSide): PeriodIfBlank | undefined {
    return (side === "start" ? bounds?.startIfBlank : bounds?.endIfBlank) || bounds?.ifBlank;
}

/** COALESCE(bound column, fallback) when that bound has an "if blank" fallback configured. */
function applyIfBlank(context: ReportLogicContext, modelKey: ModelKey | "", ifBlank: PeriodIfBlank | undefined, columnSql: string) {
    switch (ifBlank?.kind) {
        case "column": {
            const column = timestampColumn(context, modelKey, ifBlank.column);
            const fallback = column ? dateSqlForColumn(column) : null;
            return fallback && fallback !== columnSql ? `COALESCE(${columnSql}, ${fallback})` : columnSql;
        }
        case "today": return `COALESCE(${columnSql}, ${TODAY_SQL})`;
        case "date": return ifBlank.date ? `COALESCE(${columnSql}, ${dateSqlLiteral(ifBlank.date)})` : columnSql;
        default: return columnSql;
    }
}

/** The report's period clause for a model: each bound on its own column, with its own fallback. */
function basedOnPeriodClause(context: ReportLogicContext, report: ReportConfig, modelKey: ModelKey) {
    const basedOn = basedOnColumnFor(context, report, modelKey);
    if (!basedOn) return "1=1 -- No reporting period anchor configured for this data model";
    const bounds = periodBoundsFor(report, modelKey);
    if (!bounds) return periodClauseFor(context, report, modelKey, dateSqlForColumn(basedOn));
    const sideSql = (side: PeriodBoundSide) => {
        const column = boundColumn(context, report, modelKey, side) || basedOn;
        return applyIfBlank(context, modelKey, boundIfBlank(bounds, side), dateSqlForColumn(column));
    };
    const startSql = sideSql("start");
    return periodBoundsClause(report, bounds, startSql, hasEndBound(report, bounds) ? sideSql("end") : startSql);
}

function periodBoundsIssues(context: ReportLogicContext, report: ReportConfig, modelKey: ModelKey): string[] {
    const bounds = periodBoundsFor(report, modelKey);
    if (!bounds) return [];
    const issues: string[] = [];
    const sides: PeriodBoundSide[] = hasEndBound(report, bounds) ? ["start", "end"] : ["start"];
    sides.forEach((side) => {
        const label = side === "start" ? "Start" : "End";
        const columnKey = side === "start" ? bounds.startColumn : bounds.endColumn;
        if (columnKey && !timestampColumn(context, modelKey, columnKey)) issues.push(`${label} Date: its date column is no longer available on this data model.`);
        const ifBlank = boundIfBlank(bounds, side);
        if (ifBlank?.kind === "column") {
            if (!ifBlank.column) issues.push(`${label} Date: choose the column to use when it is blank.`);
            else if (!timestampColumn(context, modelKey, ifBlank.column)) issues.push(`${label} Date: the "if blank" column is no longer available on this data model.`);
        }
        if (ifBlank?.kind === "date" && !/^\d{4}-\d{2}-\d{2}$/.test(ifBlank.date || "")) issues.push(`${label} Date: pick the date to use when it is blank.`);
    });
    return issues;
}

/** Plain-English read-back, e.g. "Trip Date on or after the period's first day and Drop-off Time on or before its last day". */
function periodBoundsPhrase(context: ReportLogicContext, report: ReportConfig, modelKey: ModelKey) {
    const bounds = periodBoundsFor(report, modelKey);
    if (!bounds) return "";
    const blankText = (side: PeriodBoundSide) => {
        const ifBlank = boundIfBlank(bounds, side);
        return ifBlank?.kind === "column" ? ` (or ${timestampColumn(context, modelKey, ifBlank.column)?.label || "another date"} when blank)`
            : ifBlank?.kind === "today" ? " (or today when blank)"
            : ifBlank?.kind === "date" && ifBlank.date ? ` (or ${ifBlank.date} when blank)`
            : "";
    };
    const name = (side: PeriodBoundSide) => boundColumn(context, report, modelKey, side)?.label || "the date";
    const start = `${name("start")}${blankText("start")} is ${bounds.startOperator === "gt" ? "after" : "on or after"} the period's first day`;
    const end = bounds.endOperator || defaultEndOperator(report.reportingPeriod.type);
    if (end === "none") return `${start}, with no end date`;
    return `${start}, and ${name("end")}${blankText("end")} is ${end === "lt" ? "before" : "on or before"} the period's last day`;
}

// --- Per-data-model switch ------------------------------------------------------------
// report.modelApplyReportingPeriod[modelKey] === false drops the reporting period for every
// metric on that model (Setup). Absent = applied, so older configs keep their SQL. Mirrors
// ReportSqlBuilder.ModelAppliesReportingPeriod / ModelPeriodOffClause.

// Keep the comment safe when a caller appends SQL on the same line.
const MODEL_PERIOD_OFF_CLAUSE = "1=1 /* Reporting period not applied to this data model (turned off in Setup) */";

function modelAppliesReportingPeriod(report: ReportConfig, modelKey?: ModelKey | "") {
    return !modelKey || report.modelApplyReportingPeriod?.[modelKey] !== false;
}

// --- Per-metric override --------------------------------------------------------------

function metricOverridesReportPeriod(section: MetricSection) {
    return section.periodFilter?.mode === "custom";
}

function makeMetricPeriodFilter(): MetricPeriodFilter {
    return { mode: "custom", conditionLogic: "and", conditions: [] };
}

function metricPeriodFilterLogic(section: MetricSection): "and" | "or" {
    return section.periodFilter?.conditionLogic === "or" ? "or" : "and";
}

function metricPeriodFilterExpr(context: ReportLogicContext, report: ReportConfig, section: MetricSection, useIds = false) {
    return ruleNodesExpression(context, metricFilterScope(report, section), section.periodFilter?.conditions || [], metricPeriodFilterLogic(section), useIds);
}

/** WHERE clauses that replace the report's reporting period for this metric. */
function metricPeriodFilterClauses(context: ReportLogicContext, report: ReportConfig, section: MetricSection, useIds = false): string[] {
    if (!section.periodFilter?.conditions?.length) return ["1=1 -- Reporting period not applied to this metric and no metric date filter defined"];
    const expr = metricPeriodFilterExpr(context, report, section, useIds);
    return expr ? [`(${expr}) -- Metric filter (replaces the report's reporting period)`] : ["1=1 -- Metric filter is incomplete (the report will refuse to run it)"];
}

function metricPeriodFilterOutline(context: ReportLogicContext, report: ReportConfig, section: MetricSection) {
    return ruleNodesOutline(context, metricFilterScope(report, section), section.periodFilter?.conditions || [], metricPeriodFilterLogic(section));
}

function metricPeriodFilterIssues(context: ReportLogicContext, report: ReportConfig, section: MetricSection): RuleNodeIssue[] {
    if (!metricOverridesReportPeriod(section)) return [];
    const nodes = section.periodFilter?.conditions || [];
    const issues: RuleNodeIssue[] = [];
    collectRuleNodeIssues(context, metricFilterScope(report, section), nodes, 0, [], issues);
    return issues;
}

export {
    PERIOD_START_OPERATOR_OPTIONS,
    PERIOD_END_OPERATOR_OPTIONS,
    PERIOD_IF_BLANK_OPTIONS,
    defaultEndOperator,
    makePeriodBounds,
    periodBoundsFor,
    periodBoundDates,
    periodBoundsClause,
    periodClauseFor,
    applyIfBlank,
    basedOnColumnFor,
    boundColumn,
    boundIfBlank,
    hasEndBound,
    basedOnPeriodClause,
    periodBoundsIssues,
    periodBoundsPhrase,
    MODEL_PERIOD_OFF_CLAUSE,
    modelAppliesReportingPeriod,
    metricOverridesReportPeriod,
    makeMetricPeriodFilter,
    metricPeriodFilterLogic,
    metricPeriodFilterExpr,
    metricPeriodFilterClauses,
    metricPeriodFilterOutline,
    metricPeriodFilterIssues,
};
